import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { placeOrder } from "@/lib/api";
import { toast } from "sonner";

const Checkout = () => {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", zip_code: "" });
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const field = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  const submit = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const res = await placeOrder({
        ...form,
        total,
        items: items.map((i) => ({
          slug: i.slug,
          name: i.name,
          price: i.price,
          size: i.size,
          quantity: i.quantity,
        })),
      });
      clear();
      setConfirmed(res.order_ref);
    } catch {
      toast.error("Could not reserve your order.");
    } finally {
      setPlacing(false);
    }
  };

  if (confirmed)
    return (
      <div data-testid="order-confirmed" className="px-5 md:px-10 py-32 text-center min-h-[60vh]">
        <CheckCircle2 className="w-12 h-12 text-[#5B2A86] mx-auto mb-6" strokeWidth={1.5} />
        <h1 className="font-serif text-5xl uppercase tracking-tighter mb-4">Order Reserved</h1>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
          Reference: {confirmed}
        </p>
        <p className="font-mono text-sm text-muted-foreground max-w-md mx-auto mb-10">
          Online payment is coming soon. We'll reach out by email to complete your order.
        </p>
        <Link
          to="/shop"
          data-testid="confirmed-continue-button"
          className="inline-block bg-foreground text-background px-10 py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#5B2A86] hover:text-foreground transition-colors"
        >
          Continue
        </Link>
      </div>
    );

  if (items.length === 0)
    return (
      <div className="px-5 md:px-10 py-32 text-center min-h-[50vh]">
        <h1 className="font-serif text-4xl uppercase mb-6">Nothing to check out</h1>
        <Link to="/shop" className="text-xs font-mono uppercase tracking-widest text-[#5B2A86]">
          Back to shop
        </Link>
      </div>
    );

  return (
    <div data-testid="checkout-page" className="px-5 md:px-10 py-14 min-h-screen">
      <h1 className="font-serif text-6xl sm:text-7xl uppercase tracking-tighter leading-none border-b border-border pb-8 mb-10">
        Checkout
      </h1>

      <div className="grid lg:grid-cols-3 gap-12">
        <form onSubmit={submit} data-testid="checkout-form" className="lg:col-span-2 space-y-6">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#5B2A86]">
            Shipping details
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            <Input label="Full Name" testid="checkout-name" required {...field("name")} />
            <Input label="Email" type="email" testid="checkout-email" required {...field("email")} />
          </div>
          <Input label="Address" testid="checkout-address" required {...field("address")} />
          <div className="grid sm:grid-cols-2 gap-6">
            <Input label="City" testid="checkout-city" required {...field("city")} />
            <Input label="ZIP Code" testid="checkout-zip" required {...field("zip_code")} />
          </div>

          <div className="border border-border p-4 bg-secondary">
            <p className="font-mono text-xs text-muted-foreground uppercase tracking-[0.15em] leading-relaxed">
              ✦ Online card payment is coming soon. Placing this order reserves your pieces — we'll
              email you to finalize payment & shipping.
            </p>
          </div>

          <button
            type="submit"
            disabled={placing}
            data-testid="place-order-button"
            className="w-full bg-foreground text-background py-5 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#5B2A86] hover:text-foreground transition-colors disabled:opacity-50"
          >
            {placing ? "Reserving…" : "Reserve Order"}
          </button>
        </form>

        <div className="lg:col-span-1">
          <div className="border border-border p-6 sticky top-24">
            <h2 className="font-serif text-2xl uppercase mb-6">Order</h2>
            <div className="space-y-4">
              {items.map((i) => (
                <div key={`${i.slug}-${i.size}`} className="font-mono text-xs">
                  <span className="text-muted-foreground">
                    {i.name} · {i.size} × {i.quantity}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, testid, ...props }) => (
  <div>
    <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
      {label}
    </label>
    <input
      data-testid={testid}
      {...props}
      className="w-full mt-2 bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-foreground"
    />
  </div>
);

export default Checkout;
